/**
 * Renders the linking sign-in screen and the related handling.
 *
 * This is called in case a login through Moodle takes place, where an auth entry with the
 * respective email address is already existing - linking is necessary in this case.
 */

import React, {useEffect, useState} from "react";
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import {Redirect} from 'react-router-dom';
import {firebaseService} from 'components/commons/firebase/Firebase';
import SignInButton from "components/commons/elements/buttons/SignInButton";
import AccountTreeIcon from '@mui/icons-material/AccountTree';

import {useStyles} from "./SignInSectionLinking.css";
import useLinkingSignIn from "./useLinkingSignIn";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import {useIntl} from "react-intl";


export default function SignInSectionLinking() {
    const classes = useStyles()
    const {
        linkBySigningInWithGivenProvider,
        linkBySigningInWithEmailAndPassword,
        linkingSuccess,
        commonEmail,
        existingAuthProvider
    } = useLinkingSignIn(new firebaseService.auth.GoogleAuthProvider())

    const [authEmail, setAuthEmail] = useState('');
    const [authPassword, setAuthPassword] = useState('');
    const [saveButtonEnabled, setSaveButtonEnabled] = useState(false);

    // Internationalization for simple strings
    const intl = useIntl();

    useEffect(() => {
        setSaveButtonEnabled(authEmail && authPassword)
    }, [authEmail, authPassword])

    const handleChangeEmail = (event) => {
        setAuthEmail(event.target.value)
    }

    const handleChangePassword = (event) => {
        setAuthPassword(event.target.value)
    }

    // If the linking is successful, forward to the authorization gate, which takes the further handling
    // A linking success is equal to a successful login in this case (i.e. login with the already existing auth provider)
    if (linkingSuccess) {
        return <Redirect to={"/authorizedGate"}/>;
    }

    return (
        <div>
            <div className={classes.linkingInfo}>
                {intl.formatMessage({
                    id: 'app.signIn.linking.appbar.info',
                    defaultMessage: 'An account with the email address {email} already exists.'
                }, {email: commonEmail})}
            </div>

            <Container component="main" maxWidth="xs">
                <CssBaseline/>
                <div className={classes.paper}>
                    <Typography component="h1" variant="h3">
                        {intl.formatMessage({
                            id: 'app.signIn.linking.header',
                            defaultMessage: 'Account Linking'
                        })}
                    </Typography>
                    <AccountTreeIcon className={classes.linkingIcon}/>
                    <Typography className={classes.centeredText} component="h1" variant="h6">
                        {intl.formatMessage({
                            id: 'app.signIn.linking.description',
                            defaultMessage: 'Sign-in with {email}...'
                        }, {email: commonEmail})}
                    </Typography>

                    {/* Sign-in with OIDC auth providers */}
                    {
                        existingAuthProvider === 'google.com'
                        && <SignInButton provider='google' onClick={linkBySigningInWithGivenProvider}/>
                    }
                    {/* Sign-in with email/password */}
                    {
                        existingAuthProvider === 'password'
                        && <>
                            {/* Email */}
                            <TextField variant="outlined" margin="normal" fullWidth required value={authEmail}
                                       onChange={handleChangeEmail}
                                       id="reg-user-email"
                                       label={intl.formatMessage({
                                           id: 'app.signIn.linking.methodEmail.username.label',
                                           defaultMessage: 'Email'
                                       })}/>

                            {/* Password */}
                            <TextField variant="outlined" margin="normal" fullWidth required type="password"
                                       value={authPassword}
                                       onChange={handleChangePassword}
                                       id="reg-user-password"
                                       label={intl.formatMessage({
                                           id: 'app.signIn.linking.methodEmail.password.label',
                                           defaultMessage: 'Password'
                                       })}/>

                            <Button disabled={!saveButtonEnabled} type="submit" fullWidth variant="contained"
                                    onClick={() => {
                                        linkBySigningInWithEmailAndPassword(authEmail, authPassword)
                                    }}>
                                {intl.formatMessage({
                                    id: 'app.signIn.linking.methodEmail.button',
                                    defaultMessage: 'Link'
                                })}
                            </Button>
                        </>
                    }
                </div>
            </Container>
        </div>
    );
}
